import { useEffect, useMemo, useState } from 'react';
import { useElementSize } from '../lib/hooks';
import type { Comment, Item, ItemVersion, Person } from '../lib/types';
import { Icon } from '../components/Icon';
import { Composer } from './Composer';
import { MediaPins } from './MediaPins';
import { shapeOf, type MediaPin, type Shape } from './geometry';
import type { CommentDraft } from './CommentInput';
import type { NewComment } from './useComments';

interface Props {
  item: Item;
  version: ItemVersion;
  projectId: string;
  people: Person[];
  comments: Comment[];
  selectedId: string | null;
  highlightId: string | null;
  mode: 'browse' | 'comment';
  showResolved: boolean;
  canComment: boolean;
  onSelect: (id: string | null) => void;
  onHover: (id: string | null) => void;
  onCreate: (input: NewComment) => Promise<Comment>;
}

const ZOOM_STEPS = [0.25, 0.5, 0.75, 1, 1.5, 2, 3];

/** Static creative: click to pin, drag to mark an area, zoom to inspect. */
export function ImageCanvas({ item, version, projectId, people, comments, selectedId, highlightId, mode, showResolved, canComment, onSelect, onHover, onCreate }: Props) {
  const asset = version.asset;
  const [natural, setNatural] = useState({ width: asset.width || 0, height: asset.height || 0 });
  const [zoom, setZoom] = useState<'fit' | number>('fit');
  const [draft, setDraft] = useState<Shape | null>(null);
  const [failed, setFailed] = useState(false);
  const [stageRef, stage] = useElementSize<HTMLDivElement>();

  useEffect(() => {
    setNatural({ width: asset.width || 0, height: asset.height || 0 });
    setDraft(null);
    setFailed(false);
  }, [asset.id, asset.width, asset.height]);

  useEffect(() => {
    if (mode === 'browse') setDraft(null);
  }, [mode]);

  const padding = 48;
  const fitScale = natural.width && stage.width ? Math.min(1, (stage.width - padding) / natural.width, (stage.height - padding) / natural.height) : 1;
  const scale = zoom === 'fit' ? Math.max(fitScale, 0.02) : zoom;
  const width = natural.width ? Math.round(natural.width * scale) : undefined;
  const height = natural.height ? Math.round(natural.height * scale) : undefined;

  const pins = useMemo<MediaPin[]>(
    () =>
      comments
        .filter((c) => (c.anchor.type === 'point' || c.anchor.type === 'region') && (!c.versionId || c.versionId === version.id) && (showResolved || c.status !== 'resolved' || c.id === selectedId))
        .map((c) => ({ id: c.id, number: c.number, status: c.status, ...(shapeOf(c) as Shape) })),
    [comments, version.id, showResolved, selectedId],
  );

  const step = (direction: 1 | -1) => {
    const current = scale;
    const next = direction > 0 ? ZOOM_STEPS.find((z) => z > current + 0.001) : [...ZOOM_STEPS].reverse().find((z) => z < current - 0.001);
    if (next) setZoom(next);
  };

  const submit = async (input: CommentDraft) => {
    if (!draft) return;
    const isRegion = draft.width !== undefined && draft.height !== undefined;
    const comment = await onCreate({
      ...input,
      itemId: item.id,
      versionId: version.id,
      anchor: { type: isRegion ? 'region' : 'point', ...draft, mediaWidth: natural.width || null, mediaHeight: natural.height || null },
    });
    setDraft(null);
    onSelect(comment.id);
  };

  // Keep the composer on the side of the pin that has room.
  const composerStyle = draft
    ? {
        ...(draft.x + (draft.width ?? 0) > 0.55 ? { right: `calc(${(1 - draft.x) * 100}% + 20px)` } : { left: `calc(${(draft.x + (draft.width ?? 0)) * 100}% + 20px)` }),
        ...(draft.y > 0.6 ? { bottom: `${(1 - draft.y - (draft.height ?? 0)) * 100}%` } : { top: `${draft.y * 100}%` }),
      }
    : null;

  return (
    <div className="canvas">
      <div className="canvas-tools">
        <span className="muted truncate grow">
          {asset.filename}
          {natural.width ? ` · ${natural.width} × ${natural.height}` : ''}
        </span>
        <div className="row" style={{ gap: 4 }}>
          <button type="button" className="icon-btn" aria-label="Zoom out" onClick={() => step(-1)} disabled={scale <= ZOOM_STEPS[0]}>
            <Icon name="zoom-out" />
          </button>
          <span className="mono" style={{ minWidth: 48, textAlign: 'center' }}>
            {Math.round(scale * 100)}%
          </span>
          <button type="button" className="icon-btn" aria-label="Zoom in" onClick={() => step(1)} disabled={scale >= ZOOM_STEPS[ZOOM_STEPS.length - 1]}>
            <Icon name="zoom-in" />
          </button>
          <button type="button" className={`icon-btn ${zoom === 'fit' ? 'is-active' : ''}`} aria-label="Fit to screen" aria-pressed={zoom === 'fit'} title="Fit to screen" onClick={() => setZoom('fit')}>
            <Icon name="fit" />
          </button>
          <a className="icon-btn" href={asset.downloadUrl} aria-label="Download this file" title="Download this file">
            <Icon name="download" />
          </a>
        </div>
      </div>

      <div ref={stageRef} className={`canvas-stage ${mode === 'comment' ? 'is-commenting' : ''}`}>
        <div className="media-scroll" onClick={() => mode === 'browse' && onSelect(null)}>
          {failed ? (
            <div className="center-fill" role="alert">
              <Icon name="alert" className="icon-lg" />
              <h3>This image could not be displayed</h3>
              <p className="muted">The file may be in a format your browser cannot show. You can still download it.</p>
              <a className="btn btn-secondary" href={asset.downloadUrl}>
                <Icon name="download" /> Download
              </a>
            </div>
          ) : (
            <div className={`media-frame ${mode === 'comment' && canComment ? 'is-commenting' : ''}`} style={{ width, height }} onClick={(event) => event.stopPropagation()}>
              <img
                src={asset.url}
                alt={item.title}
                draggable={false}
                onLoad={(event) => {
                  const img = event.currentTarget;
                  if (!img.naturalWidth || !img.naturalHeight) return;
                  // Phone photos carry a rotation flag the browser applies; the stored size may be the unrotated one.
                  const stored = natural.width && natural.height ? natural.width / natural.height : 0;
                  const shown = img.naturalWidth / img.naturalHeight;
                  if (!stored || (asset.mime !== 'image/svg+xml' && Math.abs(stored - shown) / shown > 0.02)) setNatural({ width: img.naturalWidth, height: img.naturalHeight });
                }}
                onError={() => setFailed(true)}
              />
              <MediaPins
                pins={pins}
                draft={draft}
                selectedId={selectedId}
                highlightId={highlightId}
                commenting={mode === 'comment' && canComment}
                onPlace={(shape) => {
                  onSelect(null);
                  setDraft(shape);
                }}
                onSelect={onSelect}
                onHover={onHover}
                onBackgroundClick={() => onSelect(null)}
              >
                {draft && composerStyle && (
                  <Composer title={draft.width !== undefined ? 'Revision for this area' : 'New revision'} style={composerStyle} focusKey={`${draft.x}|${draft.y}`} projectId={projectId} people={people} onSubmit={submit} onCancel={() => setDraft(null)} />
                )}
              </MediaPins>
            </div>
          )}
        </div>
      </div>

      <div className="canvas-foot">
        <span>
          {mode === 'comment' && canComment ? (
            <span className="mode-hint">
              <Icon name="pin" size={12} /> Click to pin a revision, or drag to mark an area
            </span>
          ) : (
            <span>
              Version {version.versionNo}
              {version.note ? ` · ${version.note}` : ''}
            </span>
          )}
        </span>
        <span className="mono">{asset.mime.replace('image/', '').toUpperCase()}</span>
      </div>
    </div>
  );
}
